import React, { useState } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Collapse,
  Avatar,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tabs,
  Tab,
  Button,
  Stack,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { usePermissions } from '../context/PermissionContext';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import HistoryIcon from '@mui/icons-material/History';
import StarIcon from '@mui/icons-material/Star';
import SearchIcon from '@mui/icons-material/Search';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PaletteIcon from '@mui/icons-material/Palette';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { RiFileExcel2Fill } from 'react-icons/ri';
import { FaFilePdf, FaBrain, FaVideo, FaTags } from 'react-icons/fa';
import { IoLinkSharp, IoApps } from 'react-icons/io5';
import { MdInventory, MdLocalShipping, MdQrCode } from 'react-icons/md';

// ─── New-things data ─────────────────────────────────────────────────────
// Each entry needs a stable `id` (kebab-case slug) — that's what we record
// in localStorage when a user reads it, so the tab badge clears. Don't
// reuse IDs across entries; bump the slug if the entry is replaced.
//
// New interfaces (full pages added to the dashboard).
// Sorted newest-first; the "NEW" chip auto-applies to anything within 30 days.
const NEW_INTERFACES = [
  {
    id: 'po-distribution-whs',
    title: 'PO Distribution WHS',
    description: 'Auto-distributes incoming POs across warehouses based on sales velocity and FUTQ.',
    date: '2026-04-25',
    path: '/dashboard/po-distribution-whs',
    icon: <MdLocalShipping size={20} />,
    color: '#228B22',
  },
  {
    id: 'price-scraper-v2',
    title: 'Price Scraper — EC2 Job Queue',
    description: 'A second tab on Price Scraper that runs scrapes on the EC2 worker. Survives tab close, shows live progress to anyone who opens the page, browser-notifies on completion.',
    date: '2026-04-22',
    path: '/dashboard/price-scraper',
    icon: <FaTags size={20} />,
    color: '#FF8C00',
  },
  {
    id: 'ai-video-generator',
    title: 'AI Video Generator',
    description: 'Short product / lifestyle videos via Google Veo 3.1. Reference-image driven, 4–8 sec, 720p / 1080p / 4K, with collections + history.',
    date: '2026-04-15',
    path: '/dashboard/ai-video-generator',
    icon: <FaVideo size={20} />,
    color: '#722F37',
  },
  {
    id: 'sales-dashboard',
    title: 'Sales Dashboard',
    description: 'Real-time sales analytics by date, channel, top SKUs, and warehouse — pulls from the live order feed.',
    date: '2026-03-15',
    path: '/dashboard/sales-dashboard',
    icon: <MdInventory size={20} />,
    color: '#2FC4B2',
  },
  {
    id: 'broken-link-checker',
    title: 'Broken Link Checker',
    description: 'Bulk-tests product image and asset URLs to catch dead links before they hit the site. Reports HTTP status, redirects, and timeouts in a single export.',
    date: '2026-02-10',
    path: '/dashboard/broken-link-checker',
    icon: <IoLinkSharp size={20} />,
    color: '#4169E1',
  },
  {
    id: 'file-converter',
    title: 'File Converter',
    description: 'Convert between common document and image formats (PDF ↔ Excel, image format swaps, etc.) without leaving the dashboard.',
    date: '2026-01-20',
    path: '/dashboard/file-converter',
    icon: <IoApps size={20} />,
    color: '#FF8C00',
  },
  {
    id: 'email-campaign-builder',
    title: 'Email Campaign Builder',
    description: 'Drag-and-drop MJML email composer with auto-save history, an admin review tab, and template library — built for marketing send-outs.',
    date: '2026-02-25',
    path: '/dashboard/email-campaign-builder',
    icon: <IoApps size={20} />,
    color: '#FF8C00',
  },
];

// New features added inside existing interfaces. Each entry can target a
// specific path so users can jump straight to the feature.
const FEATURES_AND_UPDATES = [
  {
    id: 'ai-image-nano-banana-21',
    title: 'AI Image Generator — new Nano Banana 2.1 model',
    description: 'The "Image Model" dropdown now has a third option: Gemini Nano Banana 2.1, Google\'s newest stable Flash-tier image model. It improves on 3.1 Flash for text in images, consistency and realism, and it is the cheapest of the three per image. Pro stays the default. Batches made with it show a green "Nano Banana 2.1" chip in your History tab.',
    date: '2026-10-10',
    path: '/dashboard/ai-image-generator',
    icon: <AutoAwesomeIcon />,
    tags: ['AI', 'Gemini', 'Cost'],
    // Per-image pricing for all three selectable models, from Google's
    // Gemini API price list (standard tier). Same table layout as the
    // 'ai-image-model-picker' entry below, with a Nano Banana 2.1 column.
    extras: (() => {
      const cols = [
        { key: 'nb21',  label: 'Nano Banana 2.1', color: '#16A34A' },
        { key: 'flash', label: 'Flash (3.1)',     color: '#0EA5E9' },
        { key: 'pro',   label: 'Pro (3)',         color: '#D97706' },
      ];
      const rows = [
        { res: '1K', nb21: '$0.034', flash: '$0.067', pro: '$0.134' },
        { res: '2K', nb21: '$0.050', flash: '$0.101', pro: '$0.134' },
        { res: '4K', nb21: '$0.113', flash: '$0.151', pro: '$0.240' },
      ];
      const TABLE_FONT = '"Inter", "SF Pro Text", -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
      const PRICE_FONT = '"JetBrains Mono", "SF Mono", "Roboto Mono", ui-monospace, Menlo, Consolas, monospace';
      const cell = { px: 1.5, py: 0.85, borderBottom: '1px solid', borderColor: 'divider' };
      return (
        <Box sx={{ mt: 1, fontFamily: TABLE_FONT, fontVariantNumeric: 'tabular-nums' }}>
          <Typography
            variant="caption"
            sx={{ fontFamily: TABLE_FONT, fontWeight: 700, color: 'text.primary', display: 'block', mb: 0.5, letterSpacing: 0.2 }}
          >
            Pricing — standard tier, per image
          </Typography>
          <Box sx={{ overflowX: 'auto', maxWidth: 560 }}>
            <Box
              component="table"
              sx={{
                width: '100%',
                borderCollapse: 'collapse',
                border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
                overflow: 'hidden',
                fontFamily: TABLE_FONT,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              <Box
                component="thead"
                sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' }}
              >
                <Box component="tr">
                  <Box component="th" sx={{ ...cell, textAlign: 'left', fontSize: 12, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                    Resolution
                  </Box>
                  {cols.map((c) => (
                    <Box component="th" key={c.key} sx={{ ...cell, textAlign: 'right', fontSize: 12, fontWeight: 700, color: c.color, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                      {c.label}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {rows.map((r) => (
                  <Box component="tr" key={r.res}>
                    <Box component="td" sx={{ ...cell, fontSize: 13.5, fontWeight: 600, color: 'text.primary' }}>
                      {r.res}
                    </Box>
                    {cols.map((c) => (
                      <Box component="td" key={c.key} sx={{ ...cell, textAlign: 'right', fontFamily: PRICE_FONT, fontSize: 13, fontWeight: 600, color: c.color }}>
                        {r[c.key]}
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.75, fontFamily: TABLE_FONT, fontStyle: 'italic' }}
          >
            Direct Gemini API cost, no Safavieh markup. Batch generation runs at 1K and costs half the 1K price. Nano Banana 2.1 also bills a small amount of "thinking" per image on top.
          </Typography>
        </Box>
      );
    })(),
  },
  {
    id: 'ai-image-model-picker',
    title: 'AI Image Generator — pick Pro or Flash model',
    description: 'New "Image Model" dropdown at the top of Generation Settings. Pro is the safer pick for hero / catalog shots (best detail and prompt adherence). Flash trades some fidelity for speed and cost — great for quick iteration and large batches. Each batch in your History tab now shows a chip telling you which model produced it, so you can compare runs side-by-side.',
    date: '2026-05-19',
    path: '/dashboard/ai-image-generator',
    icon: <AutoAwesomeIcon />,
    tags: ['AI', 'Gemini', 'Cost'],
    // Per-image pricing for the two selectable models, sourced directly
    // from Google's Generative Language API price list. Rendered as a
    // compact table beneath the description.
    extras: (() => {
      const rows = [
        { res: '1K', flash: '$0.067', pro: '$0.134' },
        { res: '2K', flash: '$0.101', pro: '$0.134' },
        { res: '4K', flash: '$0.151', pro: '$0.240' },
      ];
      // Sans-serif stack with proper digit-alignment via tabular-nums. Used
      // for everything in the table so columns line up.
      const TABLE_FONT = '"Inter", "SF Pro Text", -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
      // Monospace stack for the price cells so the decimals align cleanly
      // even on browsers that don't honor tabular-nums (older Safari).
      const PRICE_FONT = '"JetBrains Mono", "SF Mono", "Roboto Mono", ui-monospace, Menlo, Consolas, monospace';
      return (
        <Box sx={{ mt: 1, fontFamily: TABLE_FONT, fontVariantNumeric: 'tabular-nums' }}>
          <Typography
            variant="caption"
            sx={{ fontFamily: TABLE_FONT, fontWeight: 700, color: 'text.primary', display: 'block', mb: 0.5, letterSpacing: 0.2 }}
          >
            Pricing — standard tier, per image
          </Typography>
          <Box
            component="table"
            sx={{
              width: '100%', maxWidth: 460,
              borderCollapse: 'collapse',
              border: '1px solid', borderColor: 'divider', borderRadius: 1.5,
              overflow: 'hidden',
              fontFamily: TABLE_FONT,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            <Box
              component="thead"
              sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' }}
            >
              <Box component="tr">
                <Box component="th" sx={{ px: 1.5, py: 0.85, borderBottom: '1px solid', borderColor: 'divider', textAlign: 'left',  fontSize: 12, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                  Resolution
                </Box>
                <Box component="th" sx={{ px: 1.5, py: 0.85, borderBottom: '1px solid', borderColor: 'divider', textAlign: 'right', fontSize: 12, fontWeight: 700, color: '#0EA5E9', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                  Flash (3.1)
                </Box>
                <Box component="th" sx={{ px: 1.5, py: 0.85, borderBottom: '1px solid', borderColor: 'divider', textAlign: 'right', fontSize: 12, fontWeight: 700, color: '#D97706', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                  Pro (3)
                </Box>
              </Box>
            </Box>
            <Box component="tbody">
              {rows.map((r) => (
                <Box component="tr" key={r.res}>
                  <Box component="td" sx={{ px: 1.5, py: 0.85, borderBottom: '1px solid', borderColor: 'divider', fontSize: 13.5, fontWeight: 600, color: 'text.primary' }}>
                    {r.res}
                  </Box>
                  <Box component="td" sx={{ px: 1.5, py: 0.85, borderBottom: '1px solid', borderColor: 'divider', textAlign: 'right', fontFamily: PRICE_FONT, fontSize: 13, fontWeight: 600, color: '#0EA5E9' }}>
                    {r.flash}
                  </Box>
                  <Box component="td" sx={{ px: 1.5, py: 0.85, borderBottom: '1px solid', borderColor: 'divider', textAlign: 'right', fontFamily: PRICE_FONT, fontSize: 13, fontWeight: 600, color: '#D97706' }}>
                    {r.pro}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.75, fontFamily: TABLE_FONT, fontStyle: 'italic' }}
          >
            Prices are Google's "AI Free API" tier — direct Generative Language API cost, no Safavieh markup.
          </Typography>
        </Box>
      );
    })(),
  },
  {
    id: 'accent-color-picker',
    title: 'Accent color picker',
    description: 'Hover the theme toggle (sun/moon icon) to pick a custom accent color or one of 8 presets. The chosen color drives buttons, tabs, links, and the sidebar highlight — works in both light and dark mode. Setting persists across sessions.',
    date: '2026-04-28',
    path: '/dashboard',
    icon: <PaletteIcon />,
    tags: ['UI', 'Personalization'],
  },
  {
    id: 'ai-image-product-name-toggle',
    title: 'AI Image Generator — "Use product image name" toggle',
    description: 'New toggle in Generation Settings. When on, downloads use the product image filename (e.g. "rugA.png") instead of the timestamped default. For pairs, prefers the non-detail variant. The choice is baked in at generation time so history downloads stay consistent.',
    date: '2026-04-28',
    path: '/dashboard/ai-image-generator',
    icon: <AutoAwesomeIcon />,
    tags: ['AI', 'Downloads'],
  },
  {
    id: 'trusted-device-30d',
    title: 'Trusted-device 30-day cookie',
    description: 'Tick "Trust this device for 30 days" on the 2FA screen and you won\'t be asked again on that browser for a month. Per-device, revocable, hashed server-side.',
    date: '2026-04-21',
    path: null,
    icon: <CheckCircleIcon />,
    tags: ['Security', '2FA'],
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────
const isRecent = (isoDate, days = 30) => {
  if (!isoDate) return false;
  const ms = (Date.now() - new Date(isoDate).getTime());
  return ms < days * 24 * 60 * 60 * 1000;
};

const formatDate = (isoDate) => {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

// Per-user "seen" tracking. Stored in localStorage as a JSON array of entry
// IDs the user has read. Tab badges only count entries that are recent AND
// not yet seen, so the count clears once the user views a tab and re-appears
// when a new entry is added (a new ID that's not in the seen list).
export const SEEN_KEY = 'whatsNew_seen_v3';  // bumped to clear test users' prior seen state
const loadSeen = () => {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch { return new Set(); }
};
const saveSeen = (set) => {
  try { localStorage.setItem(SEEN_KEY, JSON.stringify(Array.from(set))); } catch { /* ignore */ }
};

// Exposed to App.js so the dashboard root can decide whether to redirect a
// just-logged-in user to view-updates. True when there's at least one recent
// (within the 30-day window) entry the user hasn't read yet.
export const hasUnseenWhatsNew = () => {
  const seenSet = (() => {
    try {
      const raw = localStorage.getItem(SEEN_KEY);
      if (!raw) return new Set();
      const arr = JSON.parse(raw);
      return new Set(Array.isArray(arr) ? arr : []);
    } catch { return new Set(); }
  })();
  return [...NEW_INTERFACES, ...FEATURES_AND_UPDATES]
    .some(e => isRecent(e.date) && !seenSet.has(e.id));
};

const ViewUpdates = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { setChatOpen } = useOutletContext() || {};
  const [expandedCards, setExpandedCards] = useState({});
  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem('viewUpdates_activeTab');
    return saved !== null ? parseInt(saved, 10) : 0;
  });
  const [seen, setSeen] = useState(() => loadSeen());
  const { isAdmin, isOwner, hasAccessToPath } = usePermissions();
  const isDark = theme.palette.mode === 'dark';

  // Permission filter: hide entries that point at an interface the user
  // doesn't have access to. Entries with `path: null` (e.g. login-page
  // theme toggle, 2FA trust, generic UI tweaks) are always shown — they're
  // not gated by any single interface. /dashboard root is also always
  // shown (it's a dashboard-wide feature like the accent color).
  const pathToInterfaceSlug = (path) => {
    if (!path) return null;
    const m = path.match(/^\/dashboard\/(.+)$/);
    return m ? m[1] : null;
  };
  const canSeeEntry = (entry) => {
    if (isAdmin || isOwner) return true;
    const slug = pathToInterfaceSlug(entry.path);
    if (!slug) return true;  // global feature, no per-interface gating
    return hasAccessToPath(slug);
  };

  const visibleInterfaces = NEW_INTERFACES.filter(canSeeEntry);
  const visibleFeatures = FEATURES_AND_UPDATES.filter(canSeeEntry);

  // Per-tab unseen counts for the badge — only count entries the user can
  // actually see, so the badge never says "5 new" when only 3 are visible.
  const unseenInterfacesCount = visibleInterfaces.filter(e => isRecent(e.date) && !seen.has(e.id)).length;
  const unseenFeaturesCount = visibleFeatures.filter(e => isRecent(e.date) && !seen.has(e.id)).length;

  const handleTabChange = (_, v) => {
    setActiveTab(v);
    localStorage.setItem('viewUpdates_activeTab', String(v));
  };

  // Per-tab read tracking: the badge clears as soon as the user activates
  // that tab. Tab 1 = New Interfaces, Tab 2 = Features & Updates. Tab 0
  // (Welcome) doesn't mark anything. Runs on initial mount too, so if the
  // user lands directly on tab 1 (persisted from last visit) it clears
  // its own badge immediately — same as if they'd clicked it.
  React.useEffect(() => {
    let entries = [];
    if (activeTab === 1) entries = NEW_INTERFACES;
    else if (activeTab === 2) entries = FEATURES_AND_UPDATES;
    if (!entries.length) return;
    // Only mark entries seen that the user could actually see — preserves
    // "newness" if they later gain access to a gated interface.
    const recentIds = entries.filter(e => isRecent(e.date) && canSeeEntry(e)).map(e => e.id);
    if (!recentIds.length) return;
    setSeen(prev => {
      const next = new Set(prev);
      let changed = false;
      for (const id of recentIds) if (!next.has(id)) { next.add(id); changed = true; }
      if (changed) saveSeen(next);
      return changed ? next : prev;
    });
  }, [activeTab, isAdmin, isOwner, hasAccessToPath]);

  const toggleExpand = (id) => {
    setExpandedCards(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Interfaces per category for permission filtering
  const categoryInterfaces = {
    '/dashboard/pdf-tools': [
      'ama9999-ucc-labels', 'amazon-di-ucc-label', 'bilingual-labels', 'carton-labels',
      'expense-allocation-invoices', 'home-goods-pdf', 'pdf-manipulation', 'pdf-reports',
      'pdf-to-excel-ucc-labels', 'pallet-labels', 'pdq-full-pallet-pull-label', 'cad-invoices',
      'master-retail-rugs', 'label-printing',
    ],
    '/dashboard/excel-tools': [
      'excel-export-item-images', 'items-detail', 'po-distribution', 'po-distribution-whs', 'sales-orders-export', 'excel-reports',
      'canadian-13-provinces-freight', 'eu-freight',
    ],
    '/dashboard/link-tools': ['aws-file-links', /* 'aws-image-links-jeff', 'jeff-furniture', */ 'aws-file-links-alternate', 'wayfair-asset-upload', 'amazon-bulk-image-uploader', 'broken-link-checker'],
    '/dashboard/other-tools': [
      '48-state-freight', 'customer-style-search', 'document-translation',
      'marketing-copies', 'product-details', 'remove-background', 'price-scraper',
      'social-media-content', 'product-data-validator', 'email-campaign-builder', 'barcode-generator', 'pallet-optimizer', 'reports-scheduler',
    ],
    '/dashboard/ai-tools': ['ai-image-generator', 'ai-video-generator', 'seo-content-writer', 'ai-image-search', 'rug-room-shots'],
    '/dashboard/analytics-tools': ['sales-dashboard', 'sales-dashboard-v2', 'returns-dashboard', 'new-items', 'customer-style-gaps', 'collection-launches', 'sales-pulse', 'retail-os', 'reorder-console'],
    '/dashboard/ai-image-generator': ['ai-image-generator'],
  };

  // Tool categories for quick navigation (existing Welcome content)
  const allToolCategories = [
    { title: 'PDF Tools', description: 'Generate labels, manipulate PDFs, create shipping documents, and export reports', icon: FaFilePdf, color: '#DC143C', path: '/dashboard/pdf-tools', shortcut: '2', highlights: ['UCC Labels', 'PDF Manipulation', 'Carton Labels', 'WAL4000 PDFs'] },
    { title: 'Excel Tools', description: 'Export inventory data, item details, sales orders, and image groupings', icon: RiFileExcel2Fill, color: '#228B22', path: '/dashboard/excel-tools', shortcut: '3', highlights: ['General Imports', 'Sales Orders', 'Item Images', 'Inventory Analysis'] },
    { title: 'Link Management', description: 'Generate and manage AWS file links for sharing product images and documents', icon: IoLinkSharp, color: '#4169E1', path: '/dashboard/link-tools', shortcut: '4', highlights: ['AWS File Links', 'Image URLs', 'Shareable Links'] },
    { title: 'AI Tools', description: 'Use AI to generate product lifestyle images with customizable prompts', icon: FaBrain, color: '#722F37', path: '/dashboard/ai-image-generator', shortcut: '5', highlights: ['AI Image Generator', 'Lifestyle Images', 'Prompt Library'] },
    { title: 'Other Tools', description: 'Freight calculator, document translation, background removal, and more', icon: IoApps, color: '#FF8C00', path: '/dashboard/other-tools', shortcut: '6', highlights: ['48 State Freight', 'Remove Background', 'Marketing Copies', 'Document Translation'] },
  ];
  const toolCategories = (isAdmin || isOwner) ? allToolCategories : allToolCategories.filter(cat => {
    const interfaces = categoryInterfaces[cat.path] || [];
    return interfaces.some(iface => hasAccessToPath(iface));
  });

  // Getting started steps + shortcuts (existing content)
  const gettingStarted = [
    { id: 'navigate', title: 'Navigate with the Top Bar', summary: 'Use the category icons in the navigation bar to browse available tools.', details: 'Each icon in the top bar represents a tool category. Click any icon to see a dropdown of available tools. You can also press number keys 1-6 on your keyboard to quickly open each category.' },
    { id: 'favorites', title: 'Star Your Favorite Tools', summary: 'Click the star icon next to any tool to add it to your favorites sidebar.', details: 'Starred tools appear in the favorites sidebar on the left. Press F on your keyboard to toggle the favorites panel open and closed. This gives you one-click access to the tools you use most.' },
    { id: 'history', title: 'Track Your File History', summary: 'Every file you upload or download is saved in your history panel.', details: 'Press H on your keyboard or click the history icon to open the history sidebar. You can filter by uploads or downloads, search by filename or date, and export your history to Excel. Clicking any entry will re-download the file.' },
    { id: 'search', title: 'Search for Anything', summary: 'Use the search bar to quickly find any tool, feature, or interface.', details: 'Click the search icon in the top bar or start typing to search across all tools. Results are ranked by relevance and you can navigate them with arrow keys.' },
    { id: 'help', title: 'Ask the AI Help Center', summary: 'Click the chat bubble in the bottom-right corner for instant help.', details: 'The AI assistant knows about every tool on Theezdocs. Ask it how to use a feature, troubleshoot an issue, or get step-by-step guidance. Your chat history persists as you navigate between pages.' },
  ];

  const shortcuts = [
    { keys: '1', action: 'Open Announcements' }, { keys: '2', action: 'PDF Tools' },
    { keys: '3', action: 'Excel Tools' }, { keys: '4', action: 'Link Management' },
    { keys: '5', action: 'AI Tools' }, { keys: '6', action: 'Analytics' },
    { keys: '7', action: 'Other Tools' }, { keys: 'F', action: 'Toggle Favorites' },
    { keys: 'H', action: 'Toggle History' },
  ];

  // ─── Render helpers ─────────────────────────────────────────────────────
  const renderNewInterfaces = () => (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Recently launched interfaces on Theezdocs. Click any card to jump straight in.
      </Typography>
      <Grid container spacing={2}>
        {visibleInterfaces.map((item, idx) => (
          <Grid item xs={12} md={6} key={idx}>
            <Card
              onClick={() => item.path && navigate(item.path)}
              sx={{
                cursor: item.path ? 'pointer' : 'default',
                border: `1px solid ${theme.palette.divider}`,
                transition: 'all 0.2s',
                height: '100%',
                '&:hover': item.path ? {
                  borderColor: theme.palette.primary.main,
                  boxShadow: theme.shadows[3],
                  transform: 'translateY(-2px)',
                } : undefined,
              }}
            >
              <CardContent>
                <Stack direction="row" alignItems="flex-start" spacing={2}>
                  <Avatar sx={{ bgcolor: item.color || theme.palette.primary.main, width: 44, height: 44 }}>
                    {item.icon}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {item.title}
                      </Typography>
                      {isRecent(item.date) && (
                        <Chip label="NEW" size="small" color="primary"
                          sx={{ height: 18, fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }} />
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Added {formatDate(item.date)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                    {item.path && (
                      <Button size="small" endIcon={<ArrowForwardIcon fontSize="small" />}
                        sx={{ mt: 1.5, p: 0, minWidth: 0, textTransform: 'none', fontWeight: 600 }}
                        onClick={(e) => { e.stopPropagation(); navigate(item.path); }}>
                        Open
                      </Button>
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderFeaturesAndUpdates = () => (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        New features and improvements added to existing tools.
      </Typography>
      <Stack spacing={1.5}>
        {visibleFeatures.map((item, idx) => (
          <Card
            key={idx}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: theme.palette.primary.main,
                boxShadow: theme.shadows[2],
              },
            }}
          >
            <CardContent>
              <Stack direction="row" alignItems="flex-start" spacing={2}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
                  {item.icon || <AutoAwesomeIcon />}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {item.title}
                    </Typography>
                    {isRecent(item.date) && (
                      <Chip label="NEW" size="small" color="primary"
                        sx={{ height: 18, fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }} />
                    )}
                    {(item.tags || []).map((t) => (
                      <Chip key={t} label={t} size="small" variant="outlined"
                        sx={{ height: 18, fontSize: 10 }} />
                    ))}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {formatDate(item.date)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.description}
                  </Typography>
                  {/* Optional richer body — used by entries that want to
                      include a small table, a code snippet, or any other
                      ReactNode below the plain-text description. */}
                  {item.extras && (
                    <Box sx={{ mt: 1.5 }}>{item.extras}</Box>
                  )}
                  {item.path && (
                    <Button size="small" endIcon={<ArrowForwardIcon fontSize="small" />}
                      sx={{ mt: 1.5, p: 0, minWidth: 0, textTransform: 'none', fontWeight: 600 }}
                      onClick={() => navigate(item.path)}>
                      Try it
                    </Button>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );

  // ─── Welcome / Getting Started (original content) ─────────────────────
  const renderWelcome = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon color="primary" />
          Getting Started
        </Typography>
        {gettingStarted.map((step) => (
          <Card key={step.id} sx={{ mb: 1.5, border: `1px solid ${theme.palette.divider}`, cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { borderColor: theme.palette.primary.main, boxShadow: theme.shadows[2] } }} onClick={() => toggleExpand(step.id)}>
            <CardContent sx={{ pb: 1, '&:last-child': { pb: 1 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{step.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{step.summary}</Typography>
                </Box>
                <ExpandMoreIcon sx={{ display: 'flex', alignItems: 'center', transform: expandedCards[step.id] ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', color: 'action.active' }} />
              </Box>
              <Collapse in={expandedCards[step.id]}>
                <Box sx={{ mt: 1.5, p: 2, bgcolor: isDark ? 'grey.900' : 'grey.50', borderRadius: 1, borderLeft: `3px solid ${theme.palette.primary.main}` }}>
                  <Typography variant="body2" color="text.secondary">{step.details}</Typography>
                </Box>
              </Collapse>
            </CardContent>
          </Card>
        ))}
      </Grid>
      <Grid item xs={12} md={4}>
        <Paper elevation={0} sx={{ p: 2, mb: 3, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <KeyboardIcon color="primary" />
            Keyboard Shortcuts
          </Typography>
          <List dense sx={{ p: 0 }}>
            {shortcuts.map((s, index) => (
              <React.Fragment key={index}>
                <ListItem sx={{ px: 0, py: 0.75 }}>
                  <Chip label={s.keys} size="small" sx={{ fontWeight: 700, fontSize: '0.75rem', minWidth: 28, height: 24, mr: 1.5, bgcolor: isDark ? 'grey.800' : 'grey.200', border: `1px solid ${theme.palette.divider}`, fontFamily: 'monospace' }} />
                  <ListItemText primary={s.action} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                {index < shortcuts.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
        <Paper elevation={0} sx={{ p: 2, mb: 3, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TipsAndUpdatesIcon sx={{ color: '#FFD700' }} />
            Quick Tips
          </Typography>
          <List dense sx={{ p: 0 }}>
            {[
              { icon: <StarIcon sx={{ fontSize: 18, color: '#FFD700' }} />, text: 'Star tools you use often for quick access' },
              { icon: <HistoryIcon sx={{ fontSize: 18, color: 'info.main' }} />, text: 'All uploads and downloads are saved in your history' },
              { icon: <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />, text: 'Search works across all tools and features' },
            ].map((tip, index) => (
              <React.Fragment key={index}>
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>{tip.icon}</ListItemIcon>
                  <ListItemText primary={tip.text} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                {index < 2 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
        <Paper elevation={0} sx={{ p: 2, border: `1px solid ${theme.palette.primary.main}`, borderRadius: 2, bgcolor: `${theme.palette.primary.main}10`, cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { bgcolor: `${theme.palette.primary.main}20`, transform: 'translateY(-2px)' } }} onClick={() => setChatOpen && setChatOpen(true)}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
              <TipsAndUpdatesIcon />
            </Avatar>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Need Help?</Typography>
              <Typography variant="caption" color="text.secondary">
                Click the chat bubble in the bottom-right corner to ask our AI assistant anything
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Welcome to Theezdocs
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          Your all-in-one platform for label generation, data exports, document management, and AI-powered image tools.
        </Typography>
      </Box>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab icon={<CheckCircleIcon fontSize="small" />} iconPosition="start" label="Getting Started" />
        <Tab icon={<RocketLaunchIcon fontSize="small" />} iconPosition="start" label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            New Interfaces
            {unseenInterfacesCount > 0 && (
              <Chip label={unseenInterfacesCount} size="small"
                sx={{ height: 18, fontSize: 10, fontWeight: 700 }} color="primary" />
            )}
          </Box>
        } />
        <Tab icon={<AutoAwesomeIcon fontSize="small" />} iconPosition="start" label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            Features &amp; Updates
            {unseenFeaturesCount > 0 && (
              <Chip label={unseenFeaturesCount} size="small"
                sx={{ height: 18, fontSize: 10, fontWeight: 700 }} color="primary" />
            )}
          </Box>
        } />
      </Tabs>

      {activeTab === 0 && renderWelcome()}
      {activeTab === 1 && renderNewInterfaces()}
      {activeTab === 2 && renderFeaturesAndUpdates()}
    </Box>
  );
};

export default ViewUpdates;
